/* Palette — left rail of the Rule Builder.
 *
 * Designer-friendly ontology surface. Every chip is a real entry
 * from `docs/ontology/codegen.json` — no hand-curated lists.
 *
 * Drag semantics:
 *   - dragstart writes a typed PaletteDrag JSON to dataTransfer.
 *   - the Canvas reads it on drop and mutates the AST.
 *
 * Three groups, in priority order for an interior-design audience:
 *   1. Things    — typed node kinds (Room, SKU, Vendor, …)
 *   2. About them — number / string fields (the predicate body)
 *   3. Operators  — comparators + set / range / and / or / not /
 *                   aggregations (sum / avg / count)
 */

import { useMemo, useState } from "react";
import { Search, Layers, Tag, Sigma, Filter, ChevronDown, ChevronUp } from "lucide-react";
import codegen from "../../../../../docs/ontology/codegen.json";
import { cls } from "../../../lib/cls";
import { ComparatorSchema, AggOpSchema, type Comparator, type AggOp } from "../../../types/rule";

type FieldType = "number" | "string" | "date" | "vector" | "boolean";
interface FieldChip { path: string; kind: string; field: string; type: FieldType; unit?: string }
interface NodeChip  { kind: string; label: string; glyph?: string }

export type PaletteDrag =
  | { drag: "field";    path: string; type: FieldType; unit?: string }
  | { drag: "nodeKind"; nodeKind: string }
  | { drag: "op";       op: Comparator }
  | { drag: "agg";      op: "sum" | "avg" | "count" | "min" | "max" }
  | { drag: "logic";    kind: "and" | "or" | "not" }
  | { drag: "literal";  literalKind: "number" | "string" | "set" | "range" };

const NODE_KINDS: NodeChip[] = Object.entries(codegen.nodes ?? {}).map(([k, v]) => {
  const meta = v as { label?: string; glyph?: string; abstract?: boolean };
  if (meta.abstract) return null;
  return { kind: k, label: meta.label ?? k, ...(meta.glyph ? { glyph: meta.glyph } : {}) };
}).filter((x): x is NodeChip => x !== null);

const FIELDS: FieldChip[] = (codegen.fields as unknown as FieldChip[]) ?? [];
const FIELD_TYPES_TO_SHOW: ReadonlyArray<FieldType> = ["number", "string"];

/** SKU → "sku" (all-caps acronym), BudgetLine → "budgetLine".
 *  Mirrors `varStem` in `lib/ruleToSparql.ts` so chip tooltips and
 *  the actual SPARQL output never disagree. */
function varStem(nodeKind: string): string {
  if (nodeKind.length >= 2 && nodeKind[1] === nodeKind[1]!.toUpperCase() && nodeKind[1] !== nodeKind[1]!.toLowerCase()) {
    return nodeKind.toLowerCase();
  }
  return `${nodeKind.charAt(0).toLowerCase()}${nodeKind.slice(1)}`;
}

/* Single source: every chip enum below comes straight from the Zod
 * enums in types/rule.ts. If the AST adds a new comparator or agg op,
 * the palette grows a chip automatically — no copy/paste drift. */
const COMPARATORS: ReadonlyArray<Comparator> = ComparatorSchema.options;
const AGG_OPS: ReadonlyArray<AggOp> = AggOpSchema.options;

const LOGIC_KINDS = ["and", "or", "not"] as const;
type LogicKind = (typeof LOGIC_KINDS)[number];
const LOGIC: ReadonlyArray<{ kind: LogicKind; label: string }> =
  LOGIC_KINDS.map((kind) => ({ kind, label: kind.toUpperCase() }));

function payload(p: PaletteDrag): string { return JSON.stringify(p); }

function Chip({
  label,
  hint,
  payload: data,
  tone,
}: {
  label: string;
  hint?: string;
  payload: PaletteDrag;
  tone: "ink" | "foyr" | "fern" | "sea" | "mulberry";
}): JSX.Element {
  const toneCls =
    tone === "foyr"     ? "bg-foyr/10 text-foyr-deep border-foyr/30 hover:border-foyr/60" :
    tone === "fern"     ? "bg-fern/10 text-fern border-fern/30 hover:border-fern/60" :
    tone === "sea"      ? "bg-sea/10 text-sea border-sea/30 hover:border-sea/60" :
    tone === "mulberry" ? "bg-mulberry/10 text-mulberry border-mulberry/30 hover:border-mulberry/60" :
                          "bg-cream-dark text-ink-700 border-border hover:border-ink-300";
  return (
    <button
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-foyrmind-rule-chip", payload(data));
        e.dataTransfer.effectAllowed = "copy";
      }}
      className={cls(
        "px-2.5 py-1.5 rounded-lg text-[12px] border cursor-grab active:cursor-grabbing transition-all",
        "inline-flex items-center gap-1.5 text-left leading-tight",
        toneCls,
      )}
      title={hint}
      type="button"
    >
      {label}
    </button>
  );
}

function Section({
  title,
  icon,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon: JSX.Element;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="space-y-1.5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-ink-500 hover:text-ink-700 py-1"
        type="button"
      >
        <span className="inline-flex items-center gap-1.5">{icon}{title}</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open ? <div className="flex flex-wrap gap-1.5">{children}</div> : null}
    </div>
  );
}

export function Palette(): JSX.Element {
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const fieldsShown = useMemo<FieldChip[]>(() => {
    const base = FIELDS.filter((f) => FIELD_TYPES_TO_SHOW.includes(f.type));
    if (!ql) return base;
    return base.filter((f) => f.path.toLowerCase().includes(ql) || f.field.toLowerCase().includes(ql));
  }, [ql]);
  const nodesShown = useMemo<NodeChip[]>(() => {
    if (!ql) return NODE_KINDS;
    return NODE_KINDS.filter((n) => n.kind.toLowerCase().includes(ql) || n.label.toLowerCase().includes(ql));
  }, [ql]);

  return (
    <aside className="panel p-4 space-y-4 sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
      <div>
        <div className="eyebrow mb-2">Ontology palette</div>
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="search rooms, SKU.leadDays, …"
            className="w-full pl-8 pr-2 py-1.5 rounded-lg border border-border bg-cream-dark text-[12px] focus:outline-none focus:border-foyr/60"
          />
        </div>
      </div>

      <Section title={`Things · ${nodesShown.length}`} icon={<Layers className="w-3 h-3" />}>
        {nodesShown.map((n) => (
          <Chip
            key={n.kind}
            label={n.label}
            hint={`Drag onto a graph slot. Adds ?${varStem(n.kind)} foyr:${n.kind} to the WHERE.`}
            payload={{ drag: "nodeKind", nodeKind: n.kind }}
            tone="ink"
          />
        ))}
      </Section>

      <Section title={`Fields · ${fieldsShown.length}`} icon={<Tag className="w-3 h-3" />}>
        {fieldsShown.map((f) => (
          <Chip
            key={f.path}
            label={f.path}
            hint={`${f.type}${f.unit ? ` (${f.unit})` : ""}. Drop on the left or right side of a compare slot.`}
            payload={{ drag: "field", path: f.path, type: f.type, ...(f.unit ? { unit: f.unit } : {}) }}
            tone="sea"
          />
        ))}
      </Section>

      <Section title="Compare" icon={<Filter className="w-3 h-3" />} defaultOpen={true}>
        {COMPARATORS.map((op) => (
          <Chip key={op} label={op} hint={`Comparator ${op}`} payload={{ drag: "op", op }} tone="foyr" />
        ))}
      </Section>

      <Section title="Logic" icon={<Filter className="w-3 h-3" />} defaultOpen={false}>
        {LOGIC.map((l) => (
          <Chip key={l.kind} label={l.label} hint={`Wrap the current predicate with ${l.label}`}
            payload={{ drag: "logic", kind: l.kind }} tone="mulberry" />
        ))}
      </Section>

      <Section title="Aggregate" icon={<Sigma className="w-3 h-3" />} defaultOpen={false}>
        {AGG_OPS.map((op) => (
          <Chip key={op} label={op.toUpperCase()} hint={`Aggregate (${op}) over a typed set — drag onto LHS, then drop a field + node kind into it.`}
            payload={{ drag: "agg", op }} tone="fern" />
        ))}
      </Section>

      <p className="text-[10px] text-ink-500 leading-snug pt-1 border-t border-border">
        Everything here lives in <span className="font-mono">docs/ontology/codegen.json</span>.
        Need a new field? Add it to the TTL — the chip appears here next reload.
      </p>
    </aside>
  );
}
